import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Building2, ArrowRight, Plus, Receipt } from "lucide-react";
import { toast } from "sonner";
import { VenueStatusBadge } from "./VenueRegisterPage";

const TABS = ["overview", "book", "bookings", "invoices"];
const CITIES = ["Kingston", "Portmore", "Spanish Town", "Montego Bay", "Ocho Rios", "Negril", "Mandeville", "Port Antonio"];

export default function VenueDashboard() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [tab, setTab] = useState("overview");
    const [venue, setVenue] = useState(null);
    const [summary, setSummary] = useState(null);
    const [services, setServices] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);

    const refreshAll = async () => {
        try {
            const [{ data: v }, { data: s }, { data: sum }, { data: b }, { data: inv }] = await Promise.all([
                api.get("/venues/me"),
                api.get("/services"),
                api.get("/venues/me/summary"),
                api.get("/venues/me/bookings"),
                api.get("/venues/me/invoices"),
            ]);
            setVenue(v);
            setServices(s);
            setSummary(sum);
            setBookings(b);
            setInvoices(inv);
        } catch (e) {
            if (e.response?.status === 404 || e.response?.status === 403) {
                navigate("/venue/register");
                return;
            }
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!user) return;
        refreshAll();
    }, [user]);

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="overline text-muted-foreground">Loading dashboard...</div>
            </div>
        );
    }
    if (!venue) return null;

    return (
        <div className="min-h-screen bg-background" data-testid="venue-dashboard-page">
            <Header />
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <div className="overline mb-2">Venue Channel</div>
                        <h1 className="font-serif text-4xl">{venue.name}</h1>
                        <div className="mt-2 text-sm text-muted-foreground">{venue.venue_type.toUpperCase()} · {venue.city}</div>
                    </div>
                    <VenueStatusBadge status={venue.status} />
                </div>

                <div className="mt-10 flex gap-1 border-b border-white/10" data-testid="venue-tabs">
                    {TABS.map((t) => (
                        <button
                            key={t}
                            onClick={() => setTab(t)}
                            className={`px-5 py-3 text-xs uppercase tracking-[0.2em] transition-colors ${tab === t ? "text-primary border-b-2 border-primary" : "text-muted-foreground hover:text-foreground"}`}
                            data-testid={`venue-tab-${t}`}
                        >
                            {t}
                        </button>
                    ))}
                </div>

                <div className="mt-8">
                    {tab === "overview" && <OverviewTab summary={summary} venue={venue} />}
                    {tab === "book" && (
                        <BookForGuestTab
                            services={services}
                            venue={venue}
                            onBooked={() => { setTab("bookings"); refreshAll(); }}
                        />
                    )}
                    {tab === "bookings" && <BookingsTab bookings={bookings} />}
                    {tab === "invoices" && <InvoicesTab invoices={invoices} />}
                </div>
            </section>
            <Footer />
        </div>
    );
}

function OverviewTab({ summary, venue }) {
    if (!summary) return <div className="text-sm text-muted-foreground">No data yet.</div>;
    const cards = [
        { label: "Total bookings", value: summary.total_bookings },
        { label: "Gross revenue (JMD)", value: `$${Math.round(summary.total_revenue).toLocaleString()}` },
        { label: "Affiliate earned (JMD)", value: `$${Math.round(summary.affiliate_earned).toLocaleString()}` },
        { label: "Open to invoice (JMD)", value: `$${Math.round(summary.open_amount_to_invoice).toLocaleString()}` },
    ];
    return (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="venue-overview-grid">
            {cards.map((c) => (
                <div key={`venue-card-${c.label}`} className="p-6 bg-card border border-white/10 rounded-sm">
                    <div className="overline">{c.label}</div>
                    <div className="font-serif text-3xl mt-3 gold-text">{c.value}</div>
                </div>
            ))}
            <div className="sm:col-span-2 lg:col-span-4 p-6 bg-card border border-primary/30 rounded-sm">
                <div className="overline mb-3">Affiliate Tier</div>
                <div className="text-base">
                    Current rebate rate: <span className="gold-text font-semibold">{(summary.current_rate * 100).toFixed(0)}%</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                    Auto-upgrades to 10% once total billed crosses JMD 100,000.
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                    Invite code: <span className="gold-text font-mono">{venue.invite_code}</span>
                </div>
            </div>
        </div>
    );
}

function BookForGuestTab({ services, venue, onBooked }) {
    const [serviceId, setServiceId] = useState(services[0]?.id || "");
    const [scheduledAt, setScheduledAt] = useState("");
    const [address, setAddress] = useState(venue.address || "");
    const [city, setCity] = useState(venue.city || "Montego Bay");
    const [guestName, setGuestName] = useState("");
    const [guestRoom, setGuestRoom] = useState("");
    const [guestPhone, setGuestPhone] = useState("");
    const [hours, setHours] = useState(4);
    const [notes, setNotes] = useState("");
    const [express, setExpress] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const service = useMemo(() => services.find((s) => s.id === serviceId), [services, serviceId]);
    const isHourly = service?.pricing_model === "hourly";

    const submit = async (e) => {
        e.preventDefault();
        if (venue.status !== "approved") {
            toast.error("Your venue is not yet approved by admin.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/venues/me/bookings", {
                service_id: serviceId,
                scheduled_at: new Date(scheduledAt).toISOString(),
                address,
                city,
                notes: notes || null,
                hours: isHourly ? parseInt(hours) : null,
                express,
                guest_name: guestName,
                guest_room: guestRoom || null,
                guest_phone: guestPhone || null,
            });
            toast.success("Booking placed. We'll dispatch a pro shortly.");
            onBooked?.();
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={submit} className="bg-card border border-white/10 p-8 rounded-sm space-y-5 max-w-3xl" data-testid="venue-book-form">
            <h2 className="font-serif text-3xl">Book on behalf of a guest</h2>
            <p className="text-sm text-muted-foreground">Charges land on your monthly venue invoice — your affiliate rebate is auto-applied.</p>

            <Field label="Service *">
                <select
                    value={serviceId}
                    onChange={(e) => setServiceId(e.target.value)}
                    className="bg-input border border-white/10 h-11 rounded-sm w-full px-3 text-sm"
                    required
                    data-testid="venue-book-service-select"
                >
                    <option value="">Select a service</option>
                    {services.map((s) => (
                        <option key={s.id} value={s.id}>{s.category} · {s.name} (JMD {s.base_price})</option>
                    ))}
                </select>
            </Field>

            <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Scheduled at *">
                    <Input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} required className="bg-input border-white/10 h-11 rounded-sm" data-testid="venue-book-scheduled-input" />
                </Field>
                {isHourly && (
                    <Field label="Hours *">
                        <Input type="number" min="1" value={hours} onChange={(e) => setHours(e.target.value)} required className="bg-input border-white/10 h-11 rounded-sm" data-testid="venue-book-hours-input" />
                    </Field>
                )}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Guest name *">
                    <Input value={guestName} onChange={(e) => setGuestName(e.target.value)} required className="bg-input border-white/10 h-11 rounded-sm" data-testid="venue-book-guest-name-input" />
                </Field>
                <Field label="Room / unit">
                    <Input value={guestRoom} onChange={(e) => setGuestRoom(e.target.value)} className="bg-input border-white/10 h-11 rounded-sm" data-testid="venue-book-room-input" />
                </Field>
            </div>

            <Field label="Guest phone (kept private)">
                <Input value={guestPhone} onChange={(e) => setGuestPhone(e.target.value)} className="bg-input border-white/10 h-11 rounded-sm" data-testid="venue-book-guest-phone-input" />
            </Field>

            <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Address *">
                    <Input value={address} onChange={(e) => setAddress(e.target.value)} required className="bg-input border-white/10 h-11 rounded-sm" data-testid="venue-book-address-input" />
                </Field>
                <Field label="City *">
                    <select value={city} onChange={(e) => setCity(e.target.value)} className="bg-input border border-white/10 h-11 rounded-sm w-full px-3 text-sm" data-testid="venue-book-city-select">
                        {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                </Field>
            </div>

            <Field label="Notes for the pro">
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="bg-input border-white/10 rounded-sm" data-testid="venue-book-notes-input" />
            </Field>

            <label className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-muted-foreground cursor-pointer">
                <input type="checkbox" checked={express} onChange={(e) => setExpress(e.target.checked)} data-testid="venue-book-express" />
                Express dispatch (+ JMD 1,500)
            </label>

            <Button
                type="submit"
                disabled={submitting || venue.status !== "approved"}
                className="w-full bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs h-12"
                data-testid="venue-book-submit-btn"
            >
                {submitting ? "Booking..." : "Place Booking"} <Plus size={14} className="ml-2" />
            </Button>
        </form>
    );
}

function BookingsTab({ bookings }) {
    if (!bookings.length) {
        return <div className="text-sm text-muted-foreground py-12 text-center">No guest bookings yet.</div>;
    }
    return (
        <div className="space-y-3" data-testid="venue-bookings-list">
            {bookings.map((b) => (
                <div key={b.id} className="p-5 bg-card border border-white/10 rounded-sm grid sm:grid-cols-5 gap-3 items-center">
                    <div className="sm:col-span-2">
                        <div className="overline text-[10px]">{b.service_category}</div>
                        <div className="font-serif text-lg">{b.service_name}</div>
                        <div className="text-xs text-muted-foreground">Guest: {b.customer_name} · Room {b.guest_room || "—"}</div>
                    </div>
                    <div className="text-xs text-muted-foreground">{new Date(b.scheduled_at).toLocaleString()}</div>
                    <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Status</div>
                        <div className="text-sm">{b.status}</div>
                        <div className="text-[10px] text-muted-foreground mt-1">Billing: {b.billing_status}</div>
                    </div>
                    <div className="text-right">
                        <div className="font-serif gold-text">JMD ${Math.round(b.total_amount).toLocaleString()}</div>
                        <div className="text-[11px] text-muted-foreground">- ${Math.round(b.affiliate_cut).toLocaleString()} rebate</div>
                    </div>
                </div>
            ))}
        </div>
    );
}

function InvoicesTab({ invoices }) {
    if (!invoices.length) {
        return <div className="text-sm text-muted-foreground py-12 text-center">No invoices yet. Admin will issue your first monthly invoice once you have completed bookings.</div>;
    }
    return (
        <div className="space-y-3" data-testid="venue-invoices-list">
            {invoices.map((inv) => (
                <div key={inv.id} className="p-5 bg-card border border-white/10 rounded-sm grid sm:grid-cols-5 gap-3 items-center">
                    <div className="sm:col-span-2 flex items-center gap-3">
                        <Receipt size={18} className="gold-text" strokeWidth={1.5} />
                        <div>
                            <div className="overline text-[10px]">Invoice</div>
                            <div className="font-mono text-sm">{inv.id.slice(0, 8).toUpperCase()}</div>
                        </div>
                    </div>
                    <div className="text-xs text-muted-foreground">{new Date(inv.issued_at).toLocaleDateString()}</div>
                    <div className="text-xs">
                        <div className="text-muted-foreground">Bookings: {inv.booking_count}</div>
                        <div className="mt-1">Status: <span className={inv.status === "paid" ? "text-emerald-400" : "text-amber-300"}>{inv.status}</span></div>
                    </div>
                    <div className="text-right">
                        <div className="font-serif gold-text">JMD ${Math.round(inv.net_due).toLocaleString()}</div>
                        <div className="text-[11px] text-muted-foreground">Gross ${Math.round(inv.gross_total).toLocaleString()} − rebate ${Math.round(inv.affiliate_credit).toLocaleString()}</div>
                    </div>
                </div>
            ))}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <Label className="overline mb-2 block">{label}</Label>
            {children}
        </div>
    );
}
